/**
 * SendComposerModal — the shared send composer (IDI-485). Send opens it; it
 * IS the confirm: nothing goes out until the user presses Send here.
 *
 *   - Subject, default "Invoice <number> from <Dealer>" (E-01).
 *   - An editable message, emailed above the amount due and due date.
 *   - CC (SendComposerCc): seeded with the signed-in user, then the quote's
 *     salespersons[] or else its assignee; never the creator; cap 10.
 *
 * Invoices only for now. IDI-137 adopts it for change orders; IDI-518's Send
 * again opens it pre-filled from the last send via `initial`. The quote
 * composer is untouched (Adrian 10-20).
 */
import React, { useEffect, useState } from 'react';
import { Dialog, DialogHeader, DialogBody, DialogFooter } from '../ui/Dialog';
import { Button } from '../ui/Button';
import { Input, Textarea } from '../ui/Input';
import SendComposerCc, { dedupeEmails, isValidEmail, MAX_CC } from './SendComposerCc';

// IDI-593 (D2): a send goes to at most 25 To addresses (the server refuses more).
const MAX_TO = 25;
const TOO_MANY_TO = 'Send to 25 recipients or fewer.';

// IDI-640: the invoice's CC hint and footnote stay the defaults; a change
// order passes its own hint and no footnote.
const INVOICE_CC_HINT = "CC'd people get a copy with the invoice PDF.";
const INVOICE_FOOTNOTE = "The invoice PDF is attached. The email ends with \u201cPlease don't reply to this email.\u201d";

export function defaultInvoiceSubject(number, dealer) {
  const n = number ? `Invoice ${number}` : 'Invoice';
  return dealer ? `${n} from ${dealer}` : n;
}

/**
 * A subject confirmed before the invoice had a number ("Invoice from
 * <Dealer>", the new-invoice default) gets the real number once it exists:
 * "Invoice I-000019 from <Dealer>". Any other subject is left alone.
 */
export function withInvoiceNumber(subject, number) {
  const s = String(subject || '');
  if (!number || !/^Invoice(?= from |$)/.test(s)) return s;
  return s.replace(/^Invoice/, `Invoice ${number}`);
}

/**
 * @param {object}   props
 * @param {boolean}  props.open
 * @param {Function} props.onClose
 * @param {Function} props.onConfirm        ({subject, message, cc}) => void
 *                                          (plus `recipients` [{email, name}] with the checklist)
 * @param {string}   [props.title]
 * @param {string}   [props.defaultSubject]
 * @param {object}   [props.initial]        {subject, message, cc} (IDI-518's pre-fill)
 * @param {string}   [props.recipientsLabel] who it goes to, shown above the fields
 * @param {string}   [props.note]           extra line (e.g. the split's draft completion)
 * @param {string}   [props.subjectHint]    help under Subject (e.g. the number is added on create)
 * @param {Array}    [props.salespersons]
 * @param {Array}    [props.salespersonIds]
 * @param {string}   [props.assigneeEmail]
 * @param {boolean}  [props.sending]
 * @param {Array}    [props.recipients]     IDI-580: editable checklist rows [{id, email, name, checked}]
 *                                          (Send again only; without it the To: label shows)
 * @param {Function} [props.onRecipientsChange] (rows) => void
 * @param {number}   [props.recipientCount] IDI-593: the To count when there is no checklist
 * @param {string}   [props.ccHint]         IDI-640: help under CC (default: the invoice's)
 * @param {node}     [props.footnote]       IDI-640: the line under CC (default: the invoice's
 *                                          PDF line); null shows none
 */
export default function SendComposerModal({
  open,
  onClose,
  onConfirm,
  title = 'Send Invoice',
  defaultSubject = '',
  initial,
  recipientsLabel,
  note,
  subjectHint,
  salespersons,
  salespersonIds,
  assigneeEmail,
  sending = false,
  recipients,
  onRecipientsChange,
  recipientCount,
  ccHint = INVOICE_CC_HINT,
  footnote = INVOICE_FOOTNOTE,
}) {
  const editableTo = Array.isArray(recipients);
  const [typed, setTyped] = useState('');
  const [typedError, setTypedError] = useState('');
  const tickedCount = editableTo ? recipients.filter((r) => r.checked).length : 0;
  // IDI-593 (D2): ticked, distinct To addresses (or the caller's count).
  const toCount = editableTo
    ? new Set(recipients.filter((r) => r.checked).map((r) => String(r.email || '').trim().toLowerCase()).filter(Boolean)).size
    : (Number(recipientCount) || 0);
  const tooMany = toCount > MAX_TO;

  const toggleRecipient = (id) => {
    onRecipientsChange?.(recipients.map((r) => (r.id === id ? { ...r, checked: !r.checked } : r)));
  };
  const addTyped = () => {
    const email = typed.trim();
    if (!isValidEmail(email)) {
      setTypedError('Enter a valid email address');
      return;
    }
    const found = recipients.find((r) => String(r.email).trim().toLowerCase() === email.toLowerCase());
    onRecipientsChange?.(found
      ? recipients.map((r) => (r === found ? { ...r, checked: true } : r))
      : [...recipients, { id: `typed-${email.toLowerCase()}`, email, name: '', checked: true }]);
    setTyped('');
    setTypedError('');
  };
  const [subject, setSubject] = useState(initial?.subject || defaultSubject);
  const [message, setMessage] = useState(initial?.message || '');
  // null = not seeded yet; SendComposerCc seeds it once the directory loads.
  const [cc, setCc] = useState(Array.isArray(initial?.cc) ? initial.cc : null);

  // Each opening starts from the defaults (or the pre-fill).
  useEffect(() => {
    if (!open) return;
    setSubject(initial?.subject || defaultSubject);
    setMessage(initial?.message || '');
    setCc(Array.isArray(initial?.cc) ? initial.cc : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const submit = () => {
    // An unedited default goes out blank: the server builds "Invoice <number>
    // from <Dealer>" itself, so a not-yet-numbered invoice still gets its number.
    const s = subject.trim();
    onConfirm?.({
      subject: s === String(defaultSubject).trim() ? '' : s,
      message: message.trim(),
      cc: dedupeEmails(cc || []).slice(0, MAX_CC),
      ...(editableTo ? { recipients: recipients.filter((r) => r.checked).map((r) => ({ email: r.email, name: r.name || '' })) } : {}),
    });
  };

  return (
    <Dialog open={open} onClose={sending ? () => {} : onClose} size="lg">
      <DialogHeader>{title}</DialogHeader>
      <DialogBody className="space-y-4">
        {editableTo ? (
          <fieldset data-testid="composer-recipient-list">
            <legend className="text-sm font-medium text-content-primary mb-1">To</legend>
            <div className="space-y-1">
              {recipients.map((r) => (
                <label key={r.id} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!r.checked}
                    onChange={() => toggleRecipient(r.id)}
                    disabled={sending}
                    aria-label={r.email}
                    style={{ accentColor: 'var(--color-action-primary)' }}
                  />
                  <span className="text-content-primary">{r.name ? `${r.name} · ${r.email}` : r.email}</span>
                </label>
              ))}
            </div>
            <div className="mt-2 flex gap-2">
              <input
                type="email"
                value={typed}
                onChange={(e) => { setTyped(e.target.value); setTypedError(''); }}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTyped(); } }}
                placeholder="Add an email"
                aria-label="Add recipient email"
                disabled={sending}
                className="flex-1 rounded border border-border-default px-2 py-1 text-sm"
              />
              <Button variant="secondary" size="sm" onClick={addTyped} disabled={sending || !typed.trim()}>Add</Button>
            </div>
            {typedError && <p className="mt-1 text-xs" style={{ color: 'var(--color-error)' }}>{typedError}</p>}
          </fieldset>
        ) : recipientsLabel && (
          <p className="text-sm text-content-secondary" data-testid="composer-recipients">
            To: <span className="text-content-primary">{recipientsLabel}</span>
          </p>
        )}
        <Input
          label="Subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          disabled={sending}
          aria-label="Subject"
          maxLength={200}
        />
        {subjectHint && (
          <p className="-mt-2 text-xs text-content-secondary" data-testid="composer-subject-hint">{subjectHint}</p>
        )}
        <Textarea
          label="Message"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          disabled={sending}
          aria-label="Message"
          rows={5}
          maxLength={2000}
          placeholder="Add a message to the email (optional)"
        />
        {open && (
          <SendComposerCc
            value={cc}
            onChange={(next) => setCc(next)}
            salespersons={salespersons}
            salespersonIds={salespersonIds}
            assigneeEmail={assigneeEmail}
            disabled={sending}
            hint={ccHint}
          />
        )}
        {footnote && (
          <p className="text-xs text-content-secondary">{footnote}</p>
        )}
        {note && <p className="text-sm text-content-secondary">{note}</p>}
        {tooMany && (
          <p className="text-sm" role="alert" data-testid="composer-too-many" style={{ color: 'var(--color-error)' }}>{TOO_MANY_TO}</p>
        )}
      </DialogBody>
      <DialogFooter>
        <Button variant="ghost" onClick={onClose} disabled={sending}>Cancel</Button>
        <Button variant="primary" onClick={submit} loading={sending} disabled={sending || tooMany || (editableTo && tickedCount === 0)}
          title={tooMany ? TOO_MANY_TO : (editableTo && tickedCount === 0 ? 'Add at least one recipient' : undefined)}>Send</Button>
      </DialogFooter>
    </Dialog>
  );
}
